import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, PRIMARY_OUTLET, Router } from '@angular/router';
import { Location } from '@angular/common';
import 'rxjs/add/operator/filter';
import { Subscription } from 'rxjs/Subscription';
import { InnerMessagesService } from '../../service/inner-messages.service';
import { OrdersFilterService } from 'app/delivery/services/orders-filter.service';
import { AlertFilterService } from '../../../delivery/services/alert-filter.service';
import { Utilities } from '../../utilities/utilities.model';
import { UserV3CustomDataModel } from '@shared/models/user-v-3.model';
import { UserV3Service } from '@shared/service/user-v-3.service';
import { DateUtilities } from '@shared/utilities/date-utilities.model';
import { TranslateContext } from '@shared/pipes/translate.pipe';
import {MessageManagerService} from '@shared/service/message-manager/message-manager.service';

interface IBreadcrumb {
	label: string;
	endPointLabel: string;
	params?: Params;
	url: string;
}

@Component({
	selector: 'amd-breadcrumb',
	templateUrl: './breadcrumb.component.html',
	styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
	static URL_DETAILS_END: string = 'details';

	@Output() hideBannerEvent: EventEmitter<boolean> = new EventEmitter();
	breadcrumbs: IBreadcrumb[];
	currentURL: string;
	userFavoriteURL: string;
	subscription: Subscription;
	isRoot: boolean;
	endPoint: string;
	endPointParams: string;

	translateContextLinkItems: TranslateContext = TranslateContext.BREADCRUMBS_LINK_ITEMS;
	translateContextTitle: TranslateContext = TranslateContext.BREADCRUMBS_TITLES;

	constructor(
		public activatedRoute: ActivatedRoute,
		public router: Router,
		public location: Location,
		public userV3Service: UserV3Service,
		public innerMessagesServer: InnerMessagesService,
		private messagesService: MessageManagerService,
		public orderFilterService: OrdersFilterService,
		public alertFilterService: AlertFilterService) {
		this.breadcrumbs = [];
	}

	ngOnInit(): void {
		this.currentURL = this.location.path();
		this.manageActivatedRoute();

		this.router.events.filter(event => event instanceof NavigationEnd).subscribe(event => {
			this.currentURL = (event as NavigationEnd).urlAfterRedirects;
			this.manageActivatedRoute();
		});
		this.settingUserFavoriteURL();
	}


	//set breadcrumbs
	private manageActivatedRoute(): void {

		Utilities.setSessionID();
		//cancella messaggi al cambio route
		this.innerMessagesServer.deleteAll();
		this.messagesService.deleteAll();

		//emmette la pagina corrente
		if (this.currentURL.indexOf('redirect') < 0) {
			const end: number = this.currentURL.lastIndexOf(BreadcrumbComponent.URL_DETAILS_END);
			let eventLabel: string = this.currentURL.substring(0);
			if (end > 0) {
				eventLabel = eventLabel.substring(0, end + BreadcrumbComponent.URL_DETAILS_END.length);
			}
		}

		const root: ActivatedRoute = this.activatedRoute.root;
		this.breadcrumbs = this.getBreadcrumbs(root);
		// remove first that is redundant
		if (this.breadcrumbs.length > 1) {
			this.breadcrumbs.splice(0, 1);
		}
		if (this.breadcrumbs.length > 0) {
			this.isRoot = (this.breadcrumbs.length === 1 && this.breadcrumbs[0].label === 'newDelivery');
			if (!this.isRoot) {
				this.hideBannerEvent.emit(true);
			}
			const lastBreadcrumb: IBreadcrumb = this.breadcrumbs[this.breadcrumbs.length - 1];
			this.endPoint = (lastBreadcrumb).endPointLabel;
			if (lastBreadcrumb.params && lastBreadcrumb.params.id) {
				this.endPointParams = lastBreadcrumb.params.id;
			} else if (lastBreadcrumb.params && lastBreadcrumb.params.plate) {
				this.endPointParams = lastBreadcrumb.params.plate;
			} else if (lastBreadcrumb.params && lastBreadcrumb.params.date) {
				this.endPointParams = DateUtilities.getDayFromUTC(lastBreadcrumb.params.date);
			} else {
				this.endPointParams = null;
			}
		}
	}

	ngOnDestroy(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	settingUserFavoriteURL(): void {
		this.userFavoriteURL = this.userV3Service.getCurrentUser().getFavoritePage();
	}

	private getBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: IBreadcrumb[] = []): IBreadcrumb[] {
		const ROUTE_DATA_BREADCRUMB: string = 'breadcrumb';
		const ROUTE_DATA_END_POINT: string = 'endPoint';
		//get the child routes
		const children: ActivatedRoute[] = route.children;

		//return if there are no more children
		if (children.length === 0) {
			return breadcrumbs;
		}

		//iterate over each children
		for (const child of children) {
			//verify primary route
			if (child.outlet !== PRIMARY_OUTLET) {
				continue;
			}

			//verify the custom data property "breadcrumb" is specified on the route
			if (!child.snapshot.data.hasOwnProperty(ROUTE_DATA_BREADCRUMB)) {
				return this.getBreadcrumbs(child, url, breadcrumbs);
			}

			//get the route's URL segment
			const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');

			//append route URL to URL
			url += `/${routeURL}`;

			if (!url.endsWith('/')) {
				//add breadcrumb
				const breadcrumb: IBreadcrumb = {
					label: child.snapshot.data[ROUTE_DATA_BREADCRUMB],
					endPointLabel: child.snapshot.data[ROUTE_DATA_END_POINT],
					params: child.snapshot.params,
					url: url
				};
				breadcrumbs.push(breadcrumb);
			}
			//recursive
			return this.getBreadcrumbs(child, url, breadcrumbs);
		}
		return breadcrumbs;
	}

	setFavoritePage(event: any): void {
		const levels: string[] = [UserV3CustomDataModel.LEVEL_APP_SETTINGS, UserV3CustomDataModel.LEVEL_FAVORITE_PAGE];
		const body: { value: string } = {
			value: this.currentURL
		};
		this.userV3Service.setCustomData(levels, body).subscribe(
			() => {
				this.userFavoriteURL = this.currentURL;
			}, error => {
			});
	}

	clickItem(url: string): void {
		switch (url) {
			case '/delivery/dashboard/orders': {
				this.orderFilterService.updateFilter(null);
				break;
			}
			case '/delivery/dashboard/alerts': {
				this.alertFilterService.updateFilter(null);
				break;
			}
		}
	}
}
